import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { format } from "date-fns"
import { ru } from "date-fns/locale/ru"
import { Check, Copy, ExternalLink, Megaphone, Plus } from "lucide-react"
import { type ReactNode, useEffect, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { Link, useParams } from "react-router-dom"
import { toast } from "sonner"
import {
	audienceKeys,
	formatRelative,
	getAudienceMember,
	History,
	MemberAvatar,
	memberName,
	plural,
	rubles,
	setMemberTags,
	StateLabel,
	VariableValueView,
} from "@/entities/audience"
import { draftCache } from "@/entities/bot-builder"
import { useSessionRole } from "@/entities/session"
import { rememberRecentItem } from "@/shared/lib/recent-items"
import { cn } from "@/shared/lib/utils"
import { AccessDenied } from "@/shared/ui/access-denied"
import { Button } from "@/shared/ui/button"
import { Card, CardContent } from "@/shared/ui/card"
import { LoadErrorState } from "@/shared/ui/load-error-state"
import { Skeleton } from "@/shared/ui/skeleton"
import { StatusBadge } from "@/shared/ui/status-badge"
import { useStartBroadcast } from "@/features/send-broadcast"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover"
import { useBotWorkspace, WorkspaceSection } from "@/widgets/bot-workspace"
import { LoadSwap } from "@/shared/ui/load-swap"
import { listItem } from "@/shared/lib/motion"

const TITLE = "Пользователь"

const formatDateTime = (value: string) =>
	format(new Date(value), "d MMMM yyyy, HH:mm", { locale: ru })

function Section({ title, children }: { title: string; children: ReactNode }) {
	return (
		<section className="space-y-2">
			<h2 className="px-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">
				{title}
			</h2>
			{children}
		</section>
	)
}

/** Заглушка карточки человека: профиль, четыре плитки и разделы ответов и истории. */
function MemberSkeleton() {
	return (
		<div aria-hidden="true" className="space-y-6">
			<Card className="border-border/60">
				<CardContent className="flex items-start gap-4 p-4">
					<Skeleton className="size-14 shrink-0 rounded-full" />
					<div className="min-w-0 flex-1 space-y-1.5">
						<div className="flex h-5 items-center gap-2">
							<Skeleton className="h-3.5 w-28 rounded" />
							<Skeleton className="h-3 w-24 rounded" />
						</div>
						<div className="flex h-5 items-center">
							<Skeleton className="h-3.5 w-36 rounded" />
						</div>
						<div className="flex h-7 items-center gap-1.5">
							<Skeleton className="h-6 w-20 rounded-full" />
							<Skeleton className="h-6 w-24 rounded-full" />
						</div>
					</div>
				</CardContent>
			</Card>
			<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
				{[0, 1, 2, 3].map((index) => (
					<Card key={index} className="border-border/60">
						<CardContent className="space-y-3 p-4">
							<div className="flex h-4 items-center">
								<Skeleton className="h-3 w-24 rounded" />
							</div>
							<div className="flex h-6 items-center">
								<Skeleton className="h-4 w-32 rounded" />
							</div>
							<div className="space-y-1.5">
								{[0, 1, 2].map((row) => (
									<div key={row} className="flex h-5 items-center justify-between">
										<Skeleton className="h-3 w-20 rounded" />
										<Skeleton className="h-3 w-10 rounded" />
									</div>
								))}
							</div>
						</CardContent>
					</Card>
				))}
			</div>
			{[4, 3].map((rows, section) => (
				<div key={section} className="space-y-2">
					<div className="flex h-4 items-center px-1">
						<Skeleton className="h-3 w-24 rounded" />
					</div>
					<Card className="divide-y divide-border/60 overflow-hidden border-border/60">
						{Array.from({ length: rows }, (_, index) => (
							<div key={index} className="flex items-center gap-4 px-4 py-3">
								<Skeleton className="h-3.5 w-36 rounded" />
								<Skeleton className="h-3.5 w-48 rounded" />
							</div>
						))}
					</Card>
				</div>
			))}
		</div>
	)
}

function InfoCard({
	title,
	value,
	hint,
	rows,
}: {
	title: string
	value: ReactNode
	hint?: string
	rows?: { label: string; value: ReactNode }[]
}) {
	return (
		<Card className="border-border/60">
			<CardContent className="space-y-3 p-4">
				<p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">{title}</p>
				<div className="text-base font-semibold">{value}</div>
				{hint ? <p className="text-sm text-muted-foreground">{hint}</p> : null}
				{rows?.length ? (
					<dl className="space-y-1.5 text-sm">
						{rows.map((row) => (
							<div key={row.label} className="flex items-baseline justify-between gap-3">
								<dt className="text-muted-foreground">{row.label}</dt>
								<dd className="min-w-0 truncate text-right tabular-nums">{row.value}</dd>
							</div>
						))}
					</dl>
				) : null}
			</CardContent>
		</Card>
	)
}

/** Группы человека: отметить или снять одним нажатием, сохраняется сразу. */
function TagsEditor({
	botId,
	sessionId,
	value,
}: {
	botId: string
	sessionId: string
	value: { id: string; name: string }[]
}) {
	const queryClient = useQueryClient()
	const selected = new Set(value.map((tag) => tag.id))
	const all = draftCache.pickableTags(useBotWorkspace().draft?.draft?.tags ?? [], selected)
	const save = useMutation({
		mutationFn: (tagIds: string[]) => setMemberTags(botId, sessionId, tagIds),
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: audienceKeys.member(botId, sessionId) }),
		onError: () => toast.error("Не удалось сохранить группы"),
	})
	const toggle = (id: string) => {
		const next = new Set(selected)
		if (next.has(id)) next.delete(id)
		else next.add(id)
		save.mutate([...next])
	}
	return (
		<div className="flex flex-wrap items-center gap-1.5 pt-1">
			{value.map((tag) => (
				<StatusBadge key={tag.id} tone="info" size="tag">
					{tag.name}
				</StatusBadge>
			))}
			{all.length > 0 ? (
				<Popover>
					<PopoverTrigger asChild>
						<button
							type="button"
							className="inline-flex h-5 items-center gap-1 rounded-full border border-dashed border-border px-2 text-[11px] text-muted-foreground hover:text-foreground"
						>
							<Plus className="size-3" />
							{value.length ? "Группы" : "В группу"}
						</button>
					</PopoverTrigger>
					<PopoverContent align="start" className="w-60 p-2">
						<p className="px-2.5 pt-1 pb-2 text-xs font-medium text-muted-foreground">Группы</p>
						<div className="flex max-h-72 flex-col gap-0.5 overflow-y-auto">
							{all.map((tag) => {
								const on = selected.has(tag.id)
								return (
									<button
										key={tag.id}
										type="button"
										disabled={save.isPending}
										aria-pressed={on}
										onClick={() => toggle(tag.id)}
										className={cn(
											"flex items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-accent",
											on && "bg-accent font-medium",
										)}
									>
										<Check className={cn("size-4", on ? "opacity-100" : "opacity-0")} />
										{tag.name}
									</button>
								)
							})}
						</div>
					</PopoverContent>
				</Popover>
			) : null}
		</div>
	)
}

const dayMonth = (value: string) => format(new Date(value), "d MMM yyyy", { locale: ru })

/** Карточка пользователя бота: профиль, ответы с файлами, оплаты и история диалога. */
export function AudienceMemberPage() {
	const { botId = "", sessionId = "" } = useParams()
	const { isModerator } = useSessionRole()
	const card = useQuery({
		queryKey: audienceKeys.member(botId, sessionId),
		queryFn: () => getAudienceMember(botId, sessionId),
		enabled: isModerator && !!botId && !!sessionId,
		refetchInterval: 30_000,
	})

	const startBroadcast = useStartBroadcast(botId)
	const [showEmpty, setShowEmpty] = useState(false)
	const opened = card.data?.member
	const openedName = opened ? memberName(opened) : ""
	const openedUsername = opened?.username
	useEffect(() => {
		if (!openedName) return
		rememberRecentItem({
			kind: "person",
			id: sessionId,
			label: openedName,
			hint: openedUsername ? `@${openedUsername}` : undefined,
			to: `/admin/bots/${botId}/analytics/users/${sessionId}`,
		})
	}, [botId, sessionId, openedName, openedUsername])

	if (!isModerator) return <AccessDenied title={TITLE} />

	const data = card.data
	const member = data?.member
	const declared = new Set(data?.variables.map((variable) => variable.name))
	const rows = data
		? [
				...data.variables.map((variable) => ({
					name: variable.name,
					type: variable.type as string,
					description: variable.description,
				})),
				...Object.keys(data.member.vars)
					.filter((name) => !declared.has(name))
					.sort()
					.map((name) => ({ name, type: undefined, description: null })),
			]
		: []
	const answered = (name: string) => {
		const value = data?.member.vars[name]
		return value !== undefined && value !== null && value !== ""
	}
	const filledRows = rows.filter((row) => answered(row.name))
	const emptyRows = rows.filter((row) => !answered(row.name))
	const visibleRows = showEmpty ? [...filledRows, ...emptyRows] : filledRows

	return (
		<WorkspaceSection
			title={member ? memberName(member) : card.isLoading ? null : TITLE}
			actions={
				member && member.state !== "BLOCKED" ? (
					<Button
						className="rounded-xl"
						disabled={startBroadcast.isPending}
						onClick={() =>
							startBroadcast.mutate({
								name: `Сообщение: ${memberName(member)}`,
								filter: { sessionIds: [member.id] },
							})
						}
					>
						<Megaphone /> Написать ему
					</Button>
				) : undefined
			}
		>
			<LoadSwap loading={card.isLoading} id={data && member ? "ready" : "error"}>
				{card.isLoading ? (
					<MemberSkeleton />
				) : !data || !member ? (
					<LoadErrorState onRetry={() => void card.refetch()} />
				) : (
					<div className="space-y-6">
						<Card className="border-border/60">
							<CardContent className="flex items-start gap-4 p-4">
								<MemberAvatar member={member} size="lg" />
								<div className="min-w-0 flex-1 space-y-1.5">
									<div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
										<a
											href={
												member.username
													? `https://t.me/${member.username}`
													: `tg://user?id=${member.telegramUserId}`
											}
											target="_blank"
											rel="noreferrer"
											title="Открыть чат с ним в Telegram"
											className="inline-flex items-center gap-1 text-sm text-primary"
										>
											{member.username ? `@${member.username}` : "Открыть в Telegram"}
											<ExternalLink className="h-3 w-3" />
										</a>
										<Button
											type="button"
											variant="ghost"
											title="Скопировать Telegram ID"
											className="h-auto gap-1 p-0 text-xs text-muted-foreground hover:bg-transparent hover:text-foreground"
											onClick={() => {
												void navigator.clipboard?.writeText(member.telegramUserId)
												toast.success("ID скопирован")
											}}
										>
											ID {member.telegramUserId}
											<Copy className="h-3 w-3" />
										</Button>
									</div>
									<StateLabel state={member.state} className="text-sm" />
									<TagsEditor botId={botId} sessionId={member.id} value={data.tags} />
								</div>
							</CardContent>
						</Card>

						<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
							<InfoCard
								title="Откуда пришёл"
								value={
									member.entryLink ? (
										<Link
											to={`/admin/bots/${botId}/analytics/utm/${encodeURIComponent(member.entryLink)}`}
											className="font-mono text-sm text-primary"
										>
											{member.entryLink}
										</Link>
									) : (
										"Сам, без метки"
									)
								}
								rows={[
									{ label: "Пришёл", value: dayMonth(member.createdAt) },
									{ label: "Писал", value: formatRelative(member.lastEventAt) },
								]}
							/>
							<InfoCard
								title="Диалог"
								value={`Написал ${data.activity.messagesIn} ${plural(data.activity.messagesIn, ["раз", "раза", "раз"])}`}
								rows={[
									{ label: "От бота", value: data.activity.messagesOut },
									{ label: "Нажатий кнопок", value: data.activity.buttonClicks },
									...(member.blockedAt
										? [{ label: "Заблокировал бота", value: dayMonth(member.blockedAt) }]
										: []),
								]}
							/>
							<InfoCard
								title="В приложении"
								value={
									data.app ? (
										<span className={data.app.isChannelMember ? "text-emerald-300" : undefined}>
											{data.app.isChannelMember ? "В канале клуба" : "Не в канале"}
										</span>
									) : (
										<span className="text-muted-foreground">Нет аккаунта</span>
									)
								}
								rows={
									data.app
										? [
												{ label: "Аккаунт с", value: dayMonth(data.app.registeredAt) },
												{ label: "Просмотров", value: data.app.views },
												{ label: "В избранном", value: data.app.favorites },
												...(data.app.lastViewedAt
													? [{ label: "Смотрел", value: formatRelative(data.app.lastViewedAt) }]
													: []),
											]
										: undefined
								}
								hint={data.app ? undefined : "Аккаунта с этим Telegram ID в приложении нет"}
							/>
							<InfoCard
								title="Оплаты"
								value={
									member.paidAmount > 0 ? (
										<span className="text-emerald-300">{rubles(member.paidAmount)}</span>
									) : (
										<span className="text-muted-foreground">Не платил</span>
									)
								}
								rows={[
									{
										label: "Оплачено",
										value: data.payments.filter((payment) => payment.status === "PAID").length,
									},
									{
										label: "Не оплачено",
										value: data.payments.filter((payment) => payment.status !== "PAID").length,
									},
								]}
							/>
						</div>

						<Section title="Ответы">
							<Card className="overflow-hidden border-border/60">
								{rows.length === 0 ? (
									<p className="px-4 py-6 text-center text-sm text-muted-foreground">
										В сценарии нет переменных
									</p>
								) : (
									<dl className="divide-y divide-border/60">
										{filledRows.length === 0 && !showEmpty ? (
											<p className="px-4 py-4 text-sm text-muted-foreground">
												Пока ни на что не ответил
											</p>
										) : null}
										{/* Пустые ответы раскрываются и сворачиваются по высоте. */}
										<AnimatePresence initial={false}>
											{visibleRows.map((row) => (
												<motion.div key={row.name} {...listItem}>
													<div className="grid gap-1 px-4 py-3 sm:grid-cols-[minmax(0,180px)_1fr] sm:gap-4">
														<dt className="min-w-0">
															{row.description ? (
																<span className="block truncate text-sm text-muted-foreground">
																	{row.description}
																</span>
															) : null}
															<span
																className={cn(
																	"block truncate font-mono text-muted-foreground",
																	row.description ? "text-[11px] opacity-70" : "text-xs",
																)}
															>
																{row.name}
															</span>
														</dt>
														<dd className="min-w-0 text-sm">
															<VariableValueView
																card={data}
																botId={botId}
																name={row.name}
																type={row.type}
															/>
														</dd>
													</div>
												</motion.div>
											))}
										</AnimatePresence>
										{emptyRows.length > 0 ? (
											<Button
												type="button"
												variant="ghost"
												className="h-auto w-full rounded-none px-4 py-2.5 text-sm text-muted-foreground"
												onClick={() => setShowEmpty((value) => !value)}
											>
												{showEmpty ? "Скрыть пустые" : `Без ответа: ${emptyRows.length}`}
											</Button>
										) : null}
									</dl>
								)}
							</Card>
						</Section>

						{data.payments.length > 0 ? (
							<Section title="Оплаты">
								<Card className="overflow-hidden border-border/60">
									<ul className="divide-y divide-border/60">
										{data.payments.map((payment) => (
											<li key={payment.id} className="flex items-center gap-3 px-4 py-3">
												<div className="min-w-0 flex-1">
													<p className="truncate text-sm">{payment.title}</p>
													<p className="text-xs text-muted-foreground">
														{formatDateTime(payment.paidAt ?? payment.createdAt)} ·{" "}
														{payment.status === "PAID" ? "оплачен" : "не оплачен"}
													</p>
												</div>
												{payment.testMode ? (
													<StatusBadge tone="warning" size="tag">
														тест
													</StatusBadge>
												) : null}
												<span
													className={cn(
														"text-sm font-medium tabular-nums",
														payment.status === "PAID"
															? "text-emerald-300"
															: "text-muted-foreground",
													)}
												>
													{rubles(payment.amount)}
												</span>
											</li>
										))}
									</ul>
								</Card>
							</Section>
						) : null}

						<Section title="История диалога">
							<Card className="border-border/60">
								<History card={data} botId={botId} />
							</Card>
						</Section>
					</div>
				)}
			</LoadSwap>
		</WorkspaceSection>
	)
}
